import {
    IAccordionItemProperties,
    Accordion,
    AccordionVariant,
    createAccordion,
} from '@vfde-brix/ws10/accordion';
import {
    NO_PATTERN_BUSINESS_LOGIC,
    createComponentById,
} from '@vfde-brix/ws10/core';
import {
    renderList,
    ListType,
} from '@vfde-brix/ws10/styles';
import { ADDITIONAL_PAGE_OPTIONS } from '@vfde-sails/constants';
import { createId } from '@vfde-sails/utils';
import { startAppListening } from '../../../app/listener';
import { useAppDispatch } from '../../../app/store';
import {
    selectTechnicalDetails,
    selectIsError,
    selectDeliveryScope,
} from '../selectors';
import {
    selectActiveAccordionItemId,
    toggleAccordionItem,
} from '../slice';
import { mountTableForTechnicalDetails } from './TableForTechnicalDetails';
import { AbstractedAttributeGroup } from '@vfde-sails/glados-v2';

/**
 * Prepares the accordion for the technical details
 */
export const prepareTechnicalDetailsAccordion = (containerId: string) => {
    startAppListening({
        predicate: (_action, currentState, previousState) =>
            // In case of errors we also mount the technicalDetails accordion
            // so that at least the CMS items in it are usable.
            selectTechnicalDetails(currentState) !== selectTechnicalDetails(previousState)
            || (selectIsError(currentState) && !selectIsError(previousState))
        ,
        effect: (_action, listenerApi) => {
            // the technicalDetails accordion MUST be fully mounted every time again and again
            // so that we always read the latest highlight HTML from the DOM and don't miss changes in it
            // which could have been done by the manufacturers script in the meantime.
            // Example: Apple initializes the compare module.

            const dispatch = useAppDispatch();

            const onToggle = (itemProperties: IAccordionItemProperties) => {
                dispatch(toggleAccordionItem(itemProperties));
            };

            const technicalDetailsAcordion = createComponentById(createAccordion, containerId, { onToggle });

            /* istanbul ignore if */
            if (!technicalDetailsAcordion) {
                return;
            }

            const state = listenerApi.getState();
            const technicalDetails = selectTechnicalDetails(state);
            const deliveryScope = selectDeliveryScope(state);
            const activeAccordionItemId = selectActiveAccordionItemId(state);

            updateTechnicalDetailsAccordion(
                technicalDetailsAcordion,
                technicalDetails,
                deliveryScope,
                activeAccordionItemId,
            );
        },
    });
};

let cmsItemIds: string[];
let isFirstUpdate = true;

/**
 * Mounts the technical-details accordion
 * @param accordion The accordion component
 * @param technicalDetails TechnicalDetails data
 * @param deliveryScope deliveryScope data
 * so we can store the CMS accordion item IDs
 */
const updateTechnicalDetailsAccordion = (
    accordion: Accordion,
    technicalDetails: ReturnType<typeof selectTechnicalDetails>,
    deliveryScope: string[],
    activeAccordionItemId: string | null,
): Accordion | null => {
    const tmpContainer = document.createElement('div');
    const items: IAccordionItemProperties[] = [
        ...createTechnicalDetailsData(technicalDetails),
        createDeliveryScopeData(deliveryScope!),
    ];
    const tmpAccordion = createAccordion(tmpContainer, {
        items,
        optVariant: AccordionVariant.Flat,
        business: NO_PATTERN_BUSINESS_LOGIC,
    });
    const cmsItems = getItemsFromCMS(accordion, isFirstUpdate);

    if (isFirstUpdate) {
        cmsItemIds = cmsItems.map(item => item.stdId);
    }

    const mergedItems = [
        ...cmsItems,
        ...tmpAccordion.getPropertyByKey('items'),
    ];

    for (const item of mergedItems) {
        item.optOpen = !!activeAccordionItemId && item.stdId === activeAccordionItemId;
    }

    accordion.update({
        items: mergedItems,
        optOpenIndividually: true,
    });

    isFirstUpdate = false;

    return accordion;
};

/**
 * Reads the editorial item properties from the given accordion
 * and returns them as array. When this function is called
 * the first time `isFirstCall` must be set to true, so that
 * all items can be returned. On any following call `isFirstCall`
 * should be false so that the items will be compared to the
 * `cmsItemIds` array which contains the IDs of accordion items
 * coming from the CMS.
 * @param accordion The accordion component object
 * @param isFirstCall Boolean
 * @returns Array of accordion item properties
 */
const getItemsFromCMS = (accordion: Accordion, isFirstCall: boolean): IAccordionItemProperties[] => {
    const cmsItems: IAccordionItemProperties[] = accordion.getPropertyByKey('items');
    const items: IAccordionItemProperties[] = [];

    for (const cmsItem of cmsItems) {
        const isWhitelisted = isFirstCall || cmsItemIds.includes(cmsItem.stdId);

        if (isWhitelisted) {
            /* istanbul ignore if */
            if (!cmsItem.containerAnyComponent.length) {
                continue;
            }

            items.push(cmsItem);
        }
    }

    return items;
};

const createTechnicalDetailsData = (attributeGroups: AbstractedAttributeGroup[]): IAccordionItemProperties[] => {
    const accordionItems: IAccordionItemProperties[] = [];
    let i = 0;

    for (const attributeGroup of attributeGroups) {
        const { label } = attributeGroup;
        const tableContainer = document.createElement('div');

        mountTableForTechnicalDetails(tableContainer, label, attributeGroup.attributes);

        const stdId = createId(label, undefined, i.toString());
        const stdIconLeft = window[ADDITIONAL_PAGE_OPTIONS].technicalDetails.icons[attributeGroup.id];

        accordionItems.push({
            stdId,
            stdHeadline: label,
            containerAnyComponent: tableContainer.innerHTML,
            stdIconLeft,
        });

        i++;
    }

    return accordionItems;
};

const createDeliveryScopeData = (deliveryScope: string[]): IAccordionItemProperties => {
    const deliveryScopeElement = document.createElement('div');

    deliveryScopeElement.innerHTML = renderList(deliveryScope, ListType.Bullet);

    return {
        stdId: createId(window[ADDITIONAL_PAGE_OPTIONS].technicalDetails.headline, 'ddp-delivery-scope'),
        stdHeadline: window[ADDITIONAL_PAGE_OPTIONS].technicalDetails.headline,
        containerAnyComponent: deliveryScopeElement.innerHTML,
        stdIconLeft: 'bundles',
    };
};
